import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { configureClient } from '@whinerp/client';
import { light } from '@whinerp/ui/tokens';
import { Providers } from './app/Providers.js';
import { AppRoutes } from './app/router.js';
import './styles.css';

/**
 * Entry point.
 *
 * The generated client is pointed at the public API here and nowhere else, so
 * there is exactly one place that knows the API's address.
 */
configureClient({ baseUrl: import.meta.env.VITE_API_URL ?? '/api' });

// The browser chrome takes its colour from the tokens, not from a literal in
// the HTML, so there is one palette rather than one plus the address bar.
document.querySelector('meta[name="theme-color"]')?.setAttribute('content', light.canvas);

const container = document.getElementById('root');
if (!container) throw new Error('The page is missing its #root element.');

createRoot(container).render(
  <StrictMode>
    <Providers>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </Providers>
  </StrictMode>,
);

/**
 * The service worker, registered after the page has finished loading.
 *
 * After load, not during it: registration competes with the requests the
 * application is making to become usable, and a worker that arrives a second
 * late costs nothing while a first paint that arrives a second late is the
 * only thing anybody notices.
 *
 * Only in a built bundle. In development Vite serves modules unbundled and a
 * worker caching them produces the single most confusing failure in web work -
 * a change that is saved, compiled, and not on the screen.
 */
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js').catch((error: unknown) => {
      // Not fatal and not silent. Without a worker the application still works
      // with a connection; what is lost is opening it without one, and that is
      // worth a line in the console rather than a dialog nobody can act on.
      console.warn('[whinerp] offline support is unavailable:', error);
    });
  });
}
